import { setTime } from "../date-math"
import { parseMinutes, validateWorkingWindow, weekdayFromDate } from "../form-state"
import type { WorkingHoursByUser, WorkingHoursDay } from "../form-state"

export const normalizeWorkingHours = (value?: WorkingHoursDay[] | null): WorkingHoursDay[] => {
  const base: WorkingHoursDay[] = [
    { weekday: 0, active: true, start_time: "09:00", end_time: "17:00" },
    { weekday: 1, active: true, start_time: "09:00", end_time: "17:00" },
    { weekday: 2, active: true, start_time: "09:00", end_time: "17:00" },
    { weekday: 3, active: true, start_time: "09:00", end_time: "17:00" },
    { weekday: 4, active: true, start_time: "09:00", end_time: "17:00" },
    { weekday: 5, active: false, start_time: "09:00", end_time: "17:00" },
    { weekday: 6, active: false, start_time: "09:00", end_time: "17:00" },
  ]

  if (!value?.length) return base

  const lookup = new Map<number, WorkingHoursDay>()
  value.forEach((entry) => {
    if (typeof entry?.weekday !== "number") return

    const weekday = entry.weekday

    if (weekday < 0 || weekday > 6) return

    const normalizedActive =
      entry.active === undefined || entry.active === null ? true : Boolean(entry.active)
    lookup.set(weekday, {
      weekday,
      active: normalizedActive,
      start_time: entry.start_time || "09:00",
      end_time: entry.end_time || "17:00",
      windows: entry.windows ?? [
        { start_time: entry.start_time || "09:00", end_time: entry.end_time || "17:00" },
      ],
    })
  })

  return base.map((fallback) => {
    const entry = lookup.get(fallback.weekday) ?? fallback
    const start = parseMinutes(entry.start_time)
    const end = parseMinutes(entry.end_time)
    const windows = (
      entry.windows?.length
        ? entry.windows
        : [{ start_time: entry.start_time, end_time: entry.end_time }]
    )
      .map((window) => ({
        start_time: window.start_time,
        end_time: window.end_time,
        start: parseMinutes(window.start_time),
        end: parseMinutes(window.end_time),
      }))
      .filter((window) => window.start !== null && window.end !== null && window.start < window.end)
      .map((window) => ({ start_time: window.start_time, end_time: window.end_time }))
    const active = Boolean(entry.active && windows.length)

    return {
      weekday: fallback.weekday,
      active,
      start_time: windows[0]?.start_time ?? entry.start_time,
      end_time: windows[windows.length - 1]?.end_time ?? entry.end_time,
      windows,
    }
  })
}

export const buildWorkingHoursByUser = (
  users?: Array<{
    id?: string
    availability_policy?: {
      hours?: Array<{ weekday: number; windows?: Array<{ start_time: string; end_time: string }> }>
    } | null
  } | null>,
) => {
  const map: WorkingHoursByUser = {}
  for (const user of users ?? []) {
    if (!user?.id) continue

    const policyHours = user.availability_policy?.hours?.map((day) => ({
      weekday: day.weekday,
      active: (day.windows?.length ?? 0) > 0,
      start_time: day.windows?.[0]?.start_time ?? "09:00",
      end_time: day.windows?.[(day.windows?.length ?? 1) - 1]?.end_time ?? "17:00",
      windows: day.windows ?? [],
    }))
    map[user.id] = normalizeWorkingHours(policyHours ?? null)
  }

  return map
}

export const availabilityFor = (
  workingHoursByUser: WorkingHoursByUser,
  employeeId: string | null | undefined,
  day: Date,
) => {
  if (!employeeId) return null

  const schedule = workingHoursByUser?.[employeeId]

  if (!schedule) return null

  const weekday = weekdayFromDate(day)
  const entry = schedule.find((item) => item.weekday === weekday)

  if (!entry) return null

  if (!entry.active) return { dayOff: true }

  const windows = (entry.windows?.length ? entry.windows : [entry])
    .map((window) => ({
      startMinutes: parseMinutes(window.start_time),
      endMinutes: parseMinutes(window.end_time),
    }))
    .filter(
      (window): window is { startMinutes: number; endMinutes: number } =>
        window.startMinutes !== null &&
        window.endMinutes !== null &&
        window.startMinutes < window.endMinutes,
    )

  if (!windows.length) return { dayOff: true }

  const sortedWindows = [...windows].sort((a, b) => a.startMinutes - b.startMinutes)
  const startMinutes = Math.min(...sortedWindows.map((window) => window.startMinutes))
  const endMinutes = Math.max(...sortedWindows.map((window) => window.endMinutes))

  return {
    startMinutes,
    endMinutes,
    windows: sortedWindows,
    start: setTime(day, Math.floor(startMinutes / 60), startMinutes % 60),
    end: setTime(day, Math.floor(endMinutes / 60), endMinutes % 60),
    dayOff: false,
  }
}

export const validateAvailability = (
  workingHoursByUser: WorkingHoursByUser,
  employeeId: string | null | undefined,
  start: Date,
  end: Date,
) => validateWorkingWindow(employeeId ?? null, start, end, workingHoursByUser)
